import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { IS_MASTER, adminLogin, api, tokens } from '../lib/api';

/** The role this site is for: any admin for the console, a super admin for the master site. */
const REQUIRED_ROLE = IS_MASTER ? 'SUPER_ADMIN' : 'ADMIN';

export interface AdminUser {
  id: string;
  mobile: string;
  displayName: string | null;
  roles: string[];
}

/** What this admin may do, as the server reports it. */
export interface Access {
  permissions: string[];
  isSuperAdmin: boolean;
  role: { id: string | null; name: string };
}

/** The same expansion the server applies: `*` is everything, `area.manage` is the whole area. */
export function allows(permissions: string[], required: string): boolean {
  if (permissions.includes('*') || permissions.includes(required)) return true;
  return permissions.includes(`${required.split('.')[0]}.manage`);
}

interface AuthState {
  user: AdminUser | null;
  access: Access | null;
  /** Whether this admin holds a permission. Shapes the console; the server is what enforces it. */
  can: (permission: string) => boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<AuthState>({
  user: null,
  access: null,
  can: () => false,
  loading: true,
  signIn: async () => {},
  signOut: () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [access, setAccess] = useState<Access | null>(null);
  const [loading, setLoading] = useState(true);

  // Asked for separately from the profile: the profile is the app's too, and
  // what an admin may do is nothing the app needs to carry.
  const loadAccess = useCallback(async () => {
    try {
      setAccess(await api.get<Access>('/admin/me/access'));
    } catch {
      // Without it nothing is offered, and the server still decides.
      setAccess({ permissions: [], isSuperAdmin: false, role: { id: null, name: 'Unknown' } });
    }
  }, []);

  const signOut = useCallback(() => {
    tokens.clear();
    setUser(null);
    setAccess(null);
  }, []);

  // Restore the session on load. The stored token may have expired while the
  // tab was closed, in which case the client refreshes transparently; if that
  // fails too, this resolves to signed-out rather than a broken shell.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!tokens.access && !tokens.refresh) {
        setLoading(false);
        return;
      }
      try {
        const me = await api.get<AdminUser>('/auth/me');
        if (me.roles.includes(REQUIRED_ROLE)) await loadAccess();
        if (!cancelled) setUser(me.roles.includes(REQUIRED_ROLE) ? me : null);
      } catch {
        if (!cancelled) signOut();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [signOut, loadAccess]);

  // The API client raises this when a refresh fails mid-session.
  useEffect(() => {
    const onSignedOut = () => setUser(null);
    window.addEventListener('isb:signed-out', onSignedOut);
    return () => window.removeEventListener('isb:signed-out', onSignedOut);
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const signedIn = await adminLogin(email, password);
    if (!signedIn.roles.includes(REQUIRED_ROLE)) {
      tokens.clear();
      throw new Error(IS_MASTER ? 'Incorrect email or password.' : 'That account does not have admin access.');
    }
    await loadAccess();
    setUser(signedIn as AdminUser);
  }, [loadAccess]);

  const can = useCallback((permission: string) => allows(access?.permissions ?? [], permission), [access]);
  const value = useMemo(
    () => ({ user, access, can, loading, signIn, signOut }),
    [user, access, can, loading, signIn, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
